import { useState, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { EligibilityPicker } from './EligibilityPicker.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface Advice {
  adviceId: string;
  adviceNumber: string;
  vendorId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  claimReferenceNo?: string;
  netAmount?: number;
  transactionReferenceNo?: string;
  paymentDate?: string;
  adviceStatusCode?: string;
  emailAddress?: string;
}

/** L15.13 — Payment Advice: view / send / download a vendor payment advice. */
export function PaymentAdvicePage(): ReactNode {
  const { day } = useBusinessDay();
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const [eligId, setEligId] = useState(params.get('eligibilityId') ?? '');
  const [data, setData] = useState<Advice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = (id: string) => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    setNotice(null);
    setData(null);
    // Every advice raised for the eligibility is kept; the newest comes first.
    apiRequest<Advice[]>(`/v1/payments/advice/eligibility/${id.trim()}`, { token })
      .then((rows) => setData(rows[0] ?? null))
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const id = params.get('eligibilityId');
    if (id) load(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSend = async () => {
    if (!data?.adviceId) return;
    setBusy(true);
    setError(null);
    try {
      await apiRequest<any>(`/v1/payments/advice/${data.adviceId}/send`, { token, method: 'PATCH', body: { sendVia: 'EMAIL' } });
      setNotice(t('finance.advice.sent'));
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const handleDownload = async () => {
    if (!data?.adviceId) return;
    setBusy(true);
    setError(null);
    try {
      const info = await apiRequest<{ adviceNumber: string }>(`/v1/payments/advice/${data.adviceId}/download`, { token });
      setNotice(t('finance.advice.downloadReady', { name: info.adviceNumber }));
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const row = (label: string, value: ReactNode) => (
    <tr style={{ borderBottom: '1px solid var(--color-border-default)' }}>
      <td style={{ padding: 8, color: 'var(--color-text-muted)' }}>{label}</td>
      <td style={{ padding: 8, textAlign: 'right', fontWeight: 600 }}>{value}</td>
    </tr>
  );

  return (
    <div style={{ padding: 24, maxWidth: 640 }}>
      <h1>{t('finance.advice.title')}</h1>
      <EligibilityPicker
        value={eligId}
        onPick={(id) => { setEligId(id); setParams(id ? { eligibilityId: id } : {}); if (id) load(id); else setData(null); }}
      />
      {error && <p style={{ color: 'red' }} role="alert">{error}</p>}
      {notice && <p style={{ color: 'var(--color-success)' }}>{notice}</p>}
      {loading && <p>{t('finance.advice.loading')}</p>}
      {!loading && !data && !error && <p>{t('finance.advice.empty')}</p>}
      {data && (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid var(--color-border-default)', borderRadius: 6 }}>
            <tbody>
              {row(t('finance.advice.rowNumber'), data.adviceNumber)}
              {row(t('finance.advice.rowInvoice'), data.invoiceNumber ?? '—')}
              {row(t('finance.advice.rowInvoiceDate'), day(data.invoiceDate))}
              {row(t('finance.advice.rowClaimRef'), data.claimReferenceNo ?? '—')}
              {row(t('finance.advice.rowNet'), money(data.netAmount ?? 0))}
              {row(t('finance.advice.rowTxnRef'), data.transactionReferenceNo ?? '—')}
              {row(t('finance.advice.rowPaymentDate'), day(data.paymentDate))}
              {row(t('finance.advice.rowStatus'), data.adviceStatusCode ?? '—')}
              {row(t('finance.advice.rowEmail'), data.emailAddress ?? '—')}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={handleSend} disabled={busy} style={{ padding: '8px 16px', background: 'var(--color-success)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{t('finance.advice.sendEmail')}</button>
            <button onClick={handleDownload} disabled={busy} style={{ padding: '8px 16px', background: 'var(--color-text-link)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 6, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{t('finance.advice.downloadPdf')}</button>
          </div>
        </>
      )}
    </div>
  );
}
